import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { getArticle, getArticles, CATEGORY_STYLES, CATEGORY_LABELS } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import Newsletter from "@/components/Newsletter";

export default function ArticleDetail() {
  const { slug } = useParams();

  const { data: article, isLoading, isError } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => getArticle(slug),
    retry: false,
  });

  const { data: related = [] } = useQuery({
    queryKey: ["related", article?.category],
    queryFn: () => getArticles({ category: article.category }),
    enabled: !!article,
  });

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 animate-pulse">
        <div className="h-6 bg-[#EAE4D9] rounded w-24 mb-8" />
        <div className="h-12 bg-[#EAE4D9] rounded w-3/4 mb-6" />
        <div className="bg-[#EAE4D9] rounded-[2rem] aspect-[16/9] mb-8" />
      </div>
    );
  }

  if (isError || !article) {
    return (
      <div data-testid="article-not-found" className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h1 className="font-serif text-5xl text-[#2C3529]">Article not found</h1>
        <p className="font-sans text-[#576054] mt-4">
          The story you're looking for may have wandered off.
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#D96B43] text-white px-8 py-3.5 font-sans font-medium hover:bg-[#C25D3A] transition-colors"
        >
          <ArrowLeft size={18} /> Back to the Journal
        </Link>
      </div>
    );
  }

  const date = new Date(article.published_at).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
  const others = related.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div data-testid="article-detail-page">
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-12">
        <Link
          to="/blog"
          data-testid="back-to-blog"
          className="inline-flex items-center gap-2 font-sans text-sm uppercase tracking-wider text-[#576054] hover:text-[#D96B43] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Journal
        </Link>

        <div className="mt-8 animate-fade-up">
          <span className={`inline-block rounded-full px-3 py-1 text-xs font-sans font-medium uppercase tracking-wider ${CATEGORY_STYLES[article.category]}`}>
            {CATEGORY_LABELS[article.category]}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2C3529] mt-5 leading-[1.08]">
            {article.title}
          </h1>
          <p className="font-sans text-xl text-[#576054] mt-5 leading-relaxed">
            {article.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-7 pb-8 border-b border-[#EAE4D9] text-sm font-sans text-[#8a9184]">
            <span className="text-[#2C3529] font-medium">{article.author}</span>
            <span>{article.author_role}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} strokeWidth={1.5} /> {date}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} strokeWidth={1.5} /> {article.read_time} min read</span>
          </div>
        </div>
      </article>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 my-10">
        <div className="overflow-hidden rounded-[2.5rem] aspect-[16/9] animate-fade-up">
          <img src={article.image} alt={article.title} className="h-full w-full object-cover" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-6 article-prose">
        {article.content.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {others.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16">
          <h2 className="font-serif text-4xl tracking-tight text-[#2C3529] mb-10">
            More on {CATEGORY_LABELS[article.category]}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {others.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} />
            ))}
          </div>
        </section>
      )}

      <Newsletter />
    </div>
  );
}
